import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api, { itemsAPI, batchesAPI } from '../services/api';
import { getFromCache } from '../services/cacheService';
import { syncAllData, isSyncNeeded } from '../services/dataSync';
import { getItemName, getBatchItemName } from '../utils/itemHelpers';
import { toast } from 'react-toastify';
import axios from 'axios';
import Navbar from '../components/Navbar';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    totalValueUSD: 0,
    totalValueLBP: 0,
  });
  const [recentItems, setRecentItems] = useState([]);
  const [alertBatches, setAlertBatches] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [frozenItemsWithStock, setFrozenItemsWithStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExchangeRateModal, setShowExchangeRateModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newExchangeRate, setNewExchangeRate] = useState('');
  const [newMarketPhone, setNewMarketPhone] = useState('');
  const { user, logout, updateUser } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const getStorageValue = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };
  
  // Get saved language or use user's preferred language
  const savedLanguage = getStorageValue('language') || user?.preferred_language || 'en';
  
  // Set initial language
  useEffect(() => {
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
      if (savedLanguage === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
      }
    }
  }, [savedLanguage, i18n]);
  
  const handleLanguageChange = async (e) => {
    const newLang = e.target.value;
    
    // Update UI immediately
    i18n.changeLanguage(newLang);
    
    if (newLang === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
    
    // Update language in database
    try {
      const token = getStorageValue('access_token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/api/auth/profile/`,
        { preferred_language: newLang },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Language updated in database:', response.data);
      
      // Update user context
      if (updateUser) {
        updateUser({ ...user, preferred_language: newLang });
      }
      
      toast.success(t('languageUpdated') || 'Language updated successfully');
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error(t('error'));
    }
  };

  useEffect(() => {
    // Trigger data sync if needed
    const token = getStorageValue('access_token');
    if (token && navigator.onLine && isSyncNeeded()) {
      console.log('[Dashboard] Triggering data sync...');
      syncAllData().catch(err => {
        console.error('[Dashboard] Sync failed:', err);
      });
    }
    
    // Small delay to allow sync to start, then fetch dashboard data
    setTimeout(() => {
      fetchDashboardData();
    }, 500);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [inventoryStatsResponse, lowStockResponse, alertBatchesResponse] = await Promise.all([
        api.get('/items/inventory_stats/').catch(async (error) => {
          if (!navigator.onLine) {
            const cached = await getFromCache('INVENTORY_STATS');
            if (cached) return { data: cached };
          }
          throw error;
        }),
        itemsAPI.getLowStockItems().catch(async (error) => {
          if (!navigator.onLine) {
            const cached = await getFromCache('LOW_STOCK');
            if (cached) return { data: cached };
          }
          throw error;
        }),
        batchesAPI.getAlertBatches().catch(async (error) => {
          if (!navigator.onLine) {
            const cached = await getFromCache('BATCHES');
            if (cached) return { data: cached };
          }
          throw error;
        }),
      ]).catch(async () => {
        // All failed, try to get cached data
        return Promise.all([
          getFromCache('INVENTORY_STATS').then(d => ({ data: d || {} })),
          getFromCache('LOW_STOCK').then(d => ({ data: d || [] })),
          getFromCache('BATCHES').then(d => ({ data: d || [] })),
        ]);
      });

      const inventoryStats = inventoryStatsResponse.data.inventory || {};
      const alertBatches = alertBatchesResponse.data || [];
      const batches = inventoryStatsResponse.data.batches || [];
      const frozenItemsWithStock = inventoryStatsResponse.data.frozen_items_with_stock || [];

      setStats({
        totalItems: inventoryStats.total_items || 0,
        lowStockItems: inventoryStats.low_stock_items || 0,
        totalValueUSD: (inventoryStats.total_value_usd || 0).toFixed(2),
        totalValueLBP: (inventoryStats.total_value_lbp || 0).toFixed(2),
      });

      setRecentItems(lowStockResponse.data || []);
      setAllBatches(batches);
      setFrozenItemsWithStock(frozenItemsWithStock);
      
      // Set alert batches
      setAlertBatches(alertBatches);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  const handleUpdateExchangeRate = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/api/auth/profile/`,
        {
          exchange_rate: newExchangeRate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update user context
      if (updateUser) {
        updateUser({
          ...user,
          exchange_rate: response.data.exchange_rate
        });
      }
      
      toast.success(t('updated') || 'Updated successfully');
      setShowExchangeRateModal(false);
      setNewExchangeRate('');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUpdatePhone = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/api/auth/profile/`,
        {
          phone: newMarketPhone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update user context
      if (updateUser) {
        updateUser({
          ...user,
          market_phone: response.data.market_phone
        });
      }
      
      toast.success(t('updated') || 'Updated successfully');
      setShowPhoneModal(false);
      setNewMarketPhone('');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard">
      <Navbar />
      
      <div className="dashboard-content" style={{ padding: '20px' }}>
        <header className="dashboard-header">
          <div>
            <h1>{t('dashboard')}</h1>
            <p>{t('welcome')}, {user?.market_name}!</p>
          </div>
          <div className="header-actions">
            {user?.user_role === 'owner' && (
              <>
                <button onClick={() => {
                  setNewExchangeRate(user?.exchange_rate || '89500');
                  setShowExchangeRateModal(true);
                }} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                  💱 {t('exchangeRate')}
                </button>
                <button onClick={() => {
                  setNewMarketPhone(user?.market_phone || '');
                  setShowPhoneModal(true);
                }} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                  📞 {t('phone')}
                </button>
                {user?.username && (
                  <a
                    href={`/market/${encodeURIComponent(user.username)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                    style={{ marginRight: '10px', textDecoration: 'none' }}
                  >
                    🛒 {t('visitStore') || 'Visit Store'}
                  </a>
                )}
              </>
            )}
          </div>
        </header>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{stats.totalItems.toLocaleString('en-US')}</h3>
            <p>{t('totalItems')}</p>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{stats.lowStockItems.toLocaleString('en-US')}</h3>
            <p>{t('lowStockItems')}</p>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3 style={{ fontSize: '1.5rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{t('usdSymbol')}{parseFloat(stats.totalValueUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            <p>{t('totalValueBuying') || t('totalValue')} ({t('usd')})</p>
          </div>
        </div>
        
        <div className="stat-card success">
          <div className="stat-icon">💵</div>
          <div className="stat-content">
            <h3 style={{ fontSize: '1.5rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{parseFloat(stats.totalValueLBP).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('lbpSymbol')}</h3>
            <p>{t('totalValueBuying') || t('totalValue')} ({t('lbp')})</p>
          </div>
        </div>
      </div>

      {/* Alert Batches Section */}
      {alertBatches && alertBatches.length > 0 && (
        <div className="alert-batches-section">
          <div className="section-header">
            <h2>⏰ {t('expiringBatches')} ({alertBatches.length})</h2>
          </div>
          <div className="batches-grid">
            {alertBatches.map((batch) => (
              <div key={batch.id} className={`batch-alert-card ${batch.alert_status}`}>
                <div className="batch-alert-header">
                  <span className={`batch-status-badge badge-${batch.alert_status}`}>
                    {batch.alert_status === 'expired' ? t('expired') : t('alert')}
                  </span>
                  <span className="batch-code">{batch.batch_code}</span>
                </div>
                <div className="batch-alert-content">
                  <p><strong>{t('itemName')}:</strong> {getBatchItemName(batch, i18n.language)}</p>
                  <p><strong>{t('quantity')}:</strong> {batch.quantity}</p>
                  <p><strong>{t('productionDate')}:</strong> {new Date(batch.production_date).toLocaleDateString()}</p>
                  <p><strong>{t('expiryDate')}:</strong> {new Date(batch.expiry_date).toLocaleDateString()}</p>
                  <div className="batch-alert-content"><strong>{t('daysUntilExpiry')}  :&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;  </strong> 
                    <span style={{ 
                      marginLeft: '5px', 
                      fontWeight: 'bold',
                      color: batch.days_until_expiry < 0 ? '#e74c3c' : batch.days_until_expiry <= 7 ? '#e67e22' : '#f39c12'
                    }}> 
                      {batch.days_until_expiry < 0 ? t('expired') : batch.days_until_expiry + ' ' + t('loading')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {frozenItemsWithStock.length > 0 && (
        <div className="frozen-stock-warning-section">
          <div className="section-header">
            <h2>🧊 {t('frozenItems') || 'Frozen Items'} - {t('warning') || 'Warning'}</h2>
            <p className="frozen-stock-warning-text">
              {t('frozenStockWarningMessage') || 'These frozen items still have stock in active batches. Please clear stock or reactivate the item.'}
            </p>
          </div>
          <div className="frozen-stock-grid">
            {frozenItemsWithStock.map((row) => {
              const itemName = i18n.language === 'ar'
                ? (row.item_name_ar || row.item_name)
                : i18n.language === 'fr'
                  ? (row.item_name_fr || row.item_name)
                  : row.item_name;

              return (
                <div key={row.item_id} className="frozen-stock-card">
                  <p><strong>{t('itemName')}:</strong> {itemName}</p>
                  <p><strong>{t('quantity')}:</strong> {Number(row.total_quantity || 0).toLocaleString('en-US')}</p>
                  <p><strong>{t('batches')}:</strong> {row.batch_count}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="recent-items">
        <h2>{t('lowStockItems')}</h2>
        <div className="items-table">
          <table dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <thead>
              <tr>
                <th>{t('itemName')}</th>
                <th>{t('category')}</th>
                <th>{t('quantity')}</th>
                <th>{t('sellingPrice')}</th>
              </tr>
            </thead>
            <tbody>
              {recentItems.map((item) => {
                const displayCategoryName = i18n.language === 'ar' ? (item.category_name_ar || item.category_name || '-') : i18n.language === 'fr' ? (item.category_name_fr || item.category_name || '-') : (item.category_name || '-');
                // حساب الكمية الإجمالية من الصناديق المرتبطة بهذا العنصر
                const itemBatches = allBatches.filter(b => b.item === item.id && b.is_active) || [];
                const totalQuantity = itemBatches.reduce((sum, batch) => sum + (batch.quantity || 0), 0);
                const lastBatch = itemBatches
                  .slice()
                  .sort((a, b) => {
                    const dateDiff = new Date(b.created_at || 0) - new Date(a.created_at || 0);
                    return dateDiff !== 0 ? dateDiff : (b.id || 0) - (a.id || 0);
                  })[0];
                const lastBatchPrice = lastBatch
                  ? parseFloat(lastBatch.unit_selling_price || 0)
                  : null;
                
                return (
                  <tr key={item.id}>
                    <td>{getItemName(item, i18n.language)}</td>
                    <td>{displayCategoryName}</td>
                    <td className={totalQuantity <= item.low_stock_threshold ? 'low-stock' : ''}>
                      {totalQuantity.toLocaleString('en-US')}
                    </td>
                    <td>
                      {lastBatch && lastBatchPrice !== null ? (
                        lastBatch.currency === 'LBP'
                          ? `${lastBatchPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('lbpSymbol')}`
                          : `${t('usdSymbol')}${lastBatchPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      ) : (
                        t('noStock')
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showExchangeRateModal && (
        <div className="modal-overlay" onClick={() => setShowExchangeRateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>{t('updateExchangeRate')}</h2>
            <div className="form-group">
              <label>{t('generalExchangeRate')}</label>
              <input
                type="number"
                value={newExchangeRate}
                onChange={(e) => setNewExchangeRate(e.target.value)}
                className="form-control"
                placeholder="89500.00"
                step="0.01"
              />
              <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                {t('generalExchangeRate')}: 1 USD = {parseFloat(newExchangeRate || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} LBP
              </small>
            </div>
            <div className="modal-actions">
              <button onClick={handleUpdateExchangeRate} className="btn btn-primary">
                {t('save')}
              </button>
              <button onClick={() => setShowExchangeRateModal(false)} className="btn btn-secondary">
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPhoneModal && (
        <div className="modal-overlay" onClick={() => setShowPhoneModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>{t('updatePhone')}</h2>
            <div className="form-group">
              <label>{t('phone')}</label>
              <input
                type="text"
                value={newMarketPhone}
                onChange={(e) => setNewMarketPhone(e.target.value)}
                className="form-control"
                placeholder={t('enterPhone') || 'Enter phone number'}
              />
              <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                {t('currentPhone')}: {user?.market_phone || t('notSet')}
              </small>
            </div>
            <div className="modal-actions">
              <button onClick={handleUpdatePhone} className="btn btn-primary">
                {t('save')}
              </button>
              <button onClick={() => setShowPhoneModal(false)} className="btn btn-secondary">
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Dashboard;
